<script setup>
import MarketNoteMark from 'components/market/MarketNoteMark.vue';
import MarketReadout from 'components/market/MarketReadout.vue';
import MarketShareList from 'components/market/MarketShareList.vue';
import MarketTrendBadge from 'components/market/MarketTrendBadge.vue';
import { MARKET_MINIMUM_SAMPLE } from 'src/configs/market';
import { formatMarketCount, formatMarketMetric } from 'src/utils/market-util';
import { computed } from 'vue';

const $props = defineProps({
    hideReadout: {
        type: Boolean,
        default: false
    },
    isAvailable: {
        type: Boolean,
        default: false
    },
    // The page's own name for the place. The service names a state by its abbreviation, "VIC, Australia".
    locationName: {
        type: String,
        default: null
    },
    showScope: {
        type: Boolean,
        default: true
    },
    showTrend: {
        type: Boolean,
        default: false
    },
    snapshot: {
        type: Object,
        default: null
    }
});

const scopeLine = computed(() => {
    const location = $props.locationName || $props.snapshot?.location?.name || 'Australia';
    const classification = $props.snapshot?.classification?.name || 'Information & Communication Technology';
    return `${location} · ${classification} · Active listings`;
});

// A share drawn from one or two listings reads as a finding, "Full-time 100%", and names the listing behind it, so the
// breakdowns follow the salary rule and are withheld until the market holds the minimum sample.
const minimumSample = computed(() => {
    const reported = $props.snapshot?.salaries?.minimumSampleSize;
    return Number.isFinite(reported) ? reported : MARKET_MINIMUM_SAMPLE;
});

const isBreakdownWithheld = computed(() => {
    const jobCount = $props.snapshot?.jobCount;
    return Number.isFinite(jobCount) && jobCount > 0 && jobCount < minimumSample.value;
});

const withheldMessage = computed(() => {
    return `Withheld while fewer than ${minimumSample.value} active listings are observed `
        + `(${formatMarketMetric($props.snapshot?.jobCount)} observed).`;
});

const employmentRows = computed(() => {
    return buildBreakdownRows($props.snapshot?.employment, count => formatMarketCount(count, 'role'));
});

const workLocationRows = computed(() => {
    return buildBreakdownRows($props.snapshot?.workLocation, count => formatMarketCount(count, 'listing'));
});

const companyRows = computed(() => {
    const companies = Array.isArray($props.snapshot?.companies) ? $props.snapshot.companies.slice(0, 8) : [];
    return companies.map(company => ({
        label: company.name,
        value: company.jobCount,
        detail: formatMarketCount(company.jobCount, 'active role')
    }));
});

function buildBreakdownRows(breakdown, describeCount) {
    if (!breakdown || typeof breakdown !== 'object') {
        return [];
    }
    return Object.entries(breakdown)
        .filter(([, value]) => Number.isFinite(value?.count))
        .map(([label, value]) => ({ label, percent: value.percent, detail: describeCount(value.count) }))
        .sort((first, second) => second.percent - first.percent);
}
</script>

<template>
    <section aria-labelledby="market-snapshot-heading" class="market-snapshot">
        <div class="row items-end justify-between q-gutter-sm q-mb-lg">
            <div>
                <p v-if="$props.showScope" class="font-mono text-caption text-uppercase text-hint q-mb-xs">{{ scopeLine }}</p>
                <h2 id="market-snapshot-heading" class="text-h4 text-weight-bold q-ma-none">What active listings show</h2>
            </div>
            <MarketTrendBadge v-if="$props.showTrend" :trend="$props.snapshot?.trend"/>
        </div>
        <MarketReadout v-if="!$props.hideReadout" :snapshot="$props.snapshot"/>
        <div class="evidence-columns q-mt-xl">
            <section aria-labelledby="employment-heading">
                <h3 id="employment-heading" class="text-h5 text-weight-bold q-mt-none q-mb-xs">
                    Employment mix<MarketNoteMark :number="5"/>
                </h3>
                <p class="text-body2 text-hint q-mt-none q-mb-sm">How active roles describe their employment type.</p>
                <p v-if="isBreakdownWithheld" class="text-body2 text-hint" role="status">{{ withheldMessage }}</p>
                <MarketShareList v-else-if="employmentRows.length" :rows="employmentRows"/>
                <p v-else-if="$props.isAvailable" class="text-body2 text-hint">
                    No employment types were observed in this snapshot.
                </p>
                <p v-else class="text-body2 text-hint">Employment mix is currently unavailable.</p>
            </section>
            <section aria-labelledby="arrangement-heading">
                <h3 id="arrangement-heading" class="text-h5 text-weight-bold q-mt-none q-mb-xs">
                    Work arrangements<MarketNoteMark :number="5"/>
                </h3>
                <p class="text-body2 text-hint q-mt-none q-mb-sm">
                    Remote, hybrid and on-site labels in active listings. A listing advertised in more than one place
                    counts once for each.
                </p>
                <p v-if="isBreakdownWithheld" class="text-body2 text-hint" role="status">{{ withheldMessage }}</p>
                <MarketShareList v-else-if="workLocationRows.length" :rows="workLocationRows"/>
                <p v-else-if="$props.isAvailable" class="text-body2 text-hint">
                    No work arrangements were observed in this snapshot.
                </p>
                <p v-else class="text-body2 text-hint">Work-arrangement data is currently unavailable.</p>
            </section>
        </div>
        <section aria-labelledby="employers-heading" class="q-mt-xl">
            <div class="row items-end justify-between q-gutter-sm q-mb-sm">
                <div>
                    <h3 id="employers-heading" class="text-h5 text-weight-bold q-ma-none">
                        Advertising organisations<MarketNoteMark :number="4"/>
                    </h3>
                    <p class="text-body2 text-hint q-mt-xs q-mb-none">
                        Employers and recruitment agencies with the most active listings in this market. An agency may
                        be recruiting for employers it does not name.
                    </p>
                </div>
                <span v-if="companyRows.length && !isBreakdownWithheld" class="font-mono text-caption text-hint">
                    Top {{ companyRows.length }} shown
                </span>
            </div>
            <p v-if="isBreakdownWithheld" class="text-body2 text-hint" role="status">{{ withheldMessage }}</p>
            <MarketShareList v-else-if="companyRows.length" :rows="companyRows" relative/>
            <p v-else-if="$props.isAvailable" class="text-body2 text-hint">
                No advertising organisations were observed in this snapshot.
            </p>
            <p v-else class="text-body2 text-hint">Advertising-organisation data is currently unavailable.</p>
        </section>
        <p class="text-caption text-hint q-mt-xl q-mb-none">
            Figures are observed from active listings on Xprt Jobs: an inventory, not a census of the Australian market.
            The numbered notes explain how each measure is counted. <a href="/methodology">Read the notes</a>
        </p>
    </section>
</template>

<style lang="scss" scoped>
.market-snapshot {
    .evidence-columns {
        display: grid;
        gap: 40px 64px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: $breakpoint-sm-max) {
        .evidence-columns {
            grid-template-columns: 1fr;
        }
    }
}
</style>
